import React, { useState, memo } from "react";
import { Accordion } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDropdown } from "react-icons/io";
import { IoIosArrowDropright } from "react-icons/io";
import { IoIosArrowDown, IoMdSettings } from "react-icons/io";
import { useTranslation } from "react-i18next";

// React Icons
import { GrDocumentText } from "react-icons/gr";
import { FaUserAlt, FaQq, FaHandHoldingHeart } from "react-icons/fa";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { MdOutlineFeaturedPlayList } from "react-icons/md";
import { MdOutlineHealthAndSafety } from "react-icons/md";
import { PiNotepadThin } from "react-icons/pi";
import { LiaCalculatorSolid } from "react-icons/lia";
import { LuPersonStanding } from "react-icons/lu";
import { blobURL } from "common/StringUtils";
import { useSelector } from "react-redux";
import { useViewportWidth } from "components/GetViewPort";
import { RiHealthBookLine } from "react-icons/ri";
import { GiSuspensionBridge } from "react-icons/gi";
import { LOCALES } from "common/constants";

// Images
const SidebarLogo = `${blobURL}/fakt-logo.png`;
const tender = `${blobURL}/Invoziotender.pdf`;
const newTenderSwedish = `${blobURL}/StandardOffertInvozioIfyllningsbar.pdf`;
const contract = `${blobURL}/InvozioContracts.pdf`;
const newContractSwidish = `${blobURL}/KontraktInvozioIfyllningsbar.pdf`;

const SideBar = () => {
  let iconStyles = { color: "#535353", fontSize: "1.5em" };
  let language = localStorage.getItem("i18nextLng");
  const viewportWidth = useViewportWidth();

  const { user } = useSelector((state) => state?.auth);
  const isTourCompleted = user?.isTourCompleted;

  const menuItems = [
    {
      name: "Dashboard",
      to:
        language === LOCALES.EN_US
          ? `/dashboard/activities?lang=${LOCALES.EN_US}`
          : `/instrumentbrada/aktiviteter?lang=${LOCALES.SV_SE}`,
      image: <GrDocumentText style={iconStyles} />,
      ...(viewportWidth < 768 && {
        subMenu: [
          {
            name: "Create Invoice",
            to: language === LOCALES.EN_US
              ? `/dashboard/invoice-tool?lang=${LOCALES.EN_US}`
              : `/instrumentbrada/faktura-verktyg?lang=${LOCALES.SV_SE}`,
          },
          {
            name: "Create timesheet",
            to: language === LOCALES.EN_US
              ? `/dashboard/time-sheet?lang=${LOCALES.EN_US}`
              : `/instrumentbrada/tidrapport?lang=${LOCALES.SV_SE}`,
          },
          {
            name: "Create tender",
            to: language === LOCALES.EN_US ? tender : newTenderSwedish,
          },
          {
            name: "Write a contract",
            to: language === LOCALES.EN_US ? contract : newContractSwidish,
          },
        ],        
      }),
    },
    {
      name: "My billing",
      to: language === LOCALES.EN_US
        ? `/dashboard/economy?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/ekonomi?lang=${LOCALES.SV_SE}`,
      image: <IoIosInformationCircleOutline style={iconStyles} />,
      keyVal: "0",
      subMenu: [
        {
          name: "Bills",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/invoices?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/fakturor?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "My customers",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/customers?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/kunder?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "My Freelancers",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/freelancers?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/frilansare?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "My Timesheets",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/created-time-sheets?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/skapade-tidrapporter?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Preliminary Invoices",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/preliminary-invoices?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/preliminara-fakturor?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Salary notices",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/salaries?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/loner?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Pension",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/pension?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/pension?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Holiday pay",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/holiday-pay?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/semesterlon?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Deduction",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/supplier-ledgers?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/leverantorsreskontra?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Employer certificate",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/employer-certificates?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/arbetsgivarcertifikat?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Worked Hours",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/worked-hours?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/arbetade-timmar?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Agreement",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/agreements?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/avtal?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Terms and Conditions",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/terms-and-conditions?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/villkor?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Control data",
          to: language === LOCALES.EN_US
            ? `/dashboard/economy/incomestatements?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/ekonomi/inkomstredovisningar?lang=${LOCALES.SV_SE}`,
        },
      ],
    },
    

    {
      name: "Settings",
      to: language === LOCALES.EN_US
        ? `/dashboard/settings?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/installningar?lang=${LOCALES.SV_SE}`,
      image: <FaUserAlt style={iconStyles} />,
      keyVal: "1",
      subMenu: [
        {
          name: "Personal data",
          to: language === LOCALES.EN_US
            ? `/dashboard/settings/personal-information?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/installningar/personlig-information?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Bank information",
          to: language === LOCALES.EN_US
            ? `/dashboard/settings/bank-information?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/installningar/bankinformation?lang=${LOCALES.SV_SE}`,
        },
        {
          name: "Change password",
          to: language === LOCALES.EN_US
            ? `/dashboard/settings/change-password?lang=${LOCALES.EN_US}`
            : `/instrumentbrada/installningar/andra-losenord?lang=${LOCALES.SV_SE}`,
        },
      ],
    },
    

    {
      name: "Insurances",
      to: language === LOCALES.EN_US
        ? `/dashboard/insurances?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/forsakring?lang=${LOCALES.SV_SE}`,
      image: <MdOutlineHealthAndSafety style={iconStyles} />,
    },

    {
      name: "Health Insurance",
      to: language === LOCALES.EN_US
        ? `/dashboard/health-insurance?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/sjukforsakring?lang=${LOCALES.SV_SE}`,
      image: <RiHealthBookLine style={iconStyles} />,
    },

    {
      name: "Pension",
      to: language === LOCALES.EN_US
        ? `/dashboard/pension?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/pension?lang=${LOCALES.SV_SE}`,
      image: <GiSuspensionBridge style={iconStyles} />,
    },

    {
      name: "Private care insurance",
      to: language === LOCALES.EN_US
        ? `/dashboard/private-healthcare-insurance?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/privatvardsforsakring?lang=${LOCALES.SV_SE}`,
      image: <FaHandHoldingHeart style={iconStyles} />,
    },

    {
      name: "Good working conditions",
      to: language === LOCALES.EN_US
        ? `/dashboard/good-working-conditions?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/bra-arbetsvillkor?lang=${LOCALES.SV_SE}`,
      image: <PiNotepadThin style={iconStyles} />,
    },
    {
      name: "See Our Features",
      to: language === LOCALES.EN_US
        ? `/dashboard/function?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/funktion?lang=${LOCALES.SV_SE}`,
      image: <MdOutlineFeaturedPlayList style={iconStyles} />,
    },
    {
      name: "Calculator",
      to: language === LOCALES.EN_US
        ? `/dashboard/calculator-anchor?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/kalkylator-ankare?lang=${LOCALES.SV_SE}`,
      image: <LiaCalculatorSolid style={iconStyles} />,
    },
    {
      name: "Become an ambassador",
      to: language === LOCALES.EN_US
        ? `/dashboard/ambassador?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/ambassador?lang=${LOCALES.SV_SE}`,
      image: <LuPersonStanding style={iconStyles} />,
    },
    {
      name: "FAQ",
      to: language === LOCALES.EN_US
        ? `/dashboard/faq?lang=${LOCALES.EN_US}`
        : `/instrumentbrada/faq?lang=${LOCALES.SV_SE}`,
      image: <FaQq style={iconStyles} />,
    },   
  ];

  const { t } = useTranslation();
  const location = useLocation();
  const [showDropDown, setShowDropDown] = useState(true);
  const navigate = useNavigate();

  const isOnActivitiesPage =
    location.pathname.includes("/dashboard/activities") ||
    location.pathname.includes("/instrumentbrada/aktiviteter");
  const targetUrl = isOnActivitiesPage
    ? `/?lang=${language}`
    : language === LOCALES.EN_US
    ? `/dashboard/activities?lang=${LOCALES.EN_US}`
    : `/instrumentbrada/aktiviteter?lang=${LOCALES.SV_SE}`;

  return (
    <div className="m-fulll-width sidebar-new-design side_bar_new mt-2">
      <p
        className="mobile-sidebar"
        onClick={() => setShowDropDown(!showDropDown)}
      >
        {t("All Pages")}
        <IoIosArrowDown></IoIosArrowDown>
      </p>

      {showDropDown && (
        <aside className="d-flex flex-column p-0 h-100 dashboard_sidebar">
          <ul className="desktop-sidebar admin">
            <Link to={targetUrl} className="hidelogo justify-content-center">
              <img
                src={SidebarLogo}
                alt="site-logo"
                className="logo-new"
                loading="lazy"
              />
            </Link>
            <Accordion
              className="text-primary"
              // defaultActiveKey={!isTourCompleted ? "1" : ""}
            >
              {menuItems.map((item, index) =>
                item.subMenu ? (
                  <span key={index}>
                    <Accordion.Item
                    // eventKey={!isTourCompleted ? item.keyVal : ""}
                    >
                      <Accordion.Header
                        onClick={() => {
                          if (
                            !location.pathname.includes(item.to.split("?")[0])
                          ) {
                            item.name === "My billing" && language === LOCALES.EN_US
                              ? navigate(`/dashboard/economy/invoices?lang=${LOCALES.EN_US}`)
                              : navigate(
                                  `/instrumentbrada/ekonomi/fakturor?lang=${LOCALES.SV_SE}`
                                );
                            item.name === "Settings" &&
                              navigate(
                                language === LOCALES.EN_US
                                  ? `/dashboard/settings/personal-information?lang=${LOCALES.EN_US}`
                                  : `/instrumentbrada/installningar/personlig-information?lang=${LOCALES.SV_SE}`
                              );
                          }
                        }}
                        className={`${
                          location.pathname.includes(item.to.split("?")[0])
                            ? "selected-dropdown"
                            : ""
                        }`}
                        id={
                          item.name === "My billing"
                            ? window.innerWidth < 768
                              ? "step1"
                              : "step3"
                            : item.name === "Settings"
                            ? window.innerWidth < 768
                              ? "step2"
                              : "step4"
                            : ""
                        }
                      >
                        <span
                          className={`${
                            location.pathname.includes(item.to.split("?")[0])
                              ? "text-light"
                              : ""
                          }`}
                        >
                          <span className="icon-width">{item?.image}</span>
                          {t(item.name)}
                        </span>
                        {item.name === "My billing" &&
                          (location.pathname.includes(item.to) ? (
                            <IoIosArrowDropdown
                              size={20}
                              className="arrow-down mt-2"
                            />
                          ) : (
                            <IoIosArrowDropright
                              size={20}
                              className="arrow-down mt-2"
                            />
                          ))}
                        {item.name === "Settings" && (
                          <IoMdSettings
                            size={20}
                            className="arrow-down setting-icon"
                          />
                        )}
                      </Accordion.Header>

                      <Accordion.Body>
                        {location.pathname.includes(item.to.split("?")[0]) &&
                          // || !isTourCompleted
                          item.subMenu.map((item, index) => (
                            <Link
                              key={item.name}
                              className={`${
                                location.pathname + location.search === item.to
                                  ? "active"
                                  : ""
                              } ${
                                item.name === "Pension and Holiday pay"
                                  ? "d-flex"
                                  : ""
                              } ${index === 0 ? "mt-1" : ""}`}
                              to={item.to}
                              id={
                                item.name === "Bank information"
                                  ? window.innerWidth < 768
                                    ? "step3"
                                    : "step5"
                                  : ""
                              }
                            >
                              <span className="ml-3">{t(item.name)}</span>
                            </Link>
                          ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  </span>
                ) : (
                  <Link
                    key={item.name}
                    className={` ${
                      location.pathname + location.search === item.to
                        ? "selected"
                        : ""
                    }`}
                    id={
                      item.name === "See Our Features"
                        ? window.innerWidth < 768
                          ? "step4"
                          : "step6"
                        : item.name === "Calculator"
                        ? window.innerWidth < 768
                          ? "step5"
                          : "step7"
                        : item.name === "FAQ"
                        ? window.innerWidth < 768
                          ? "step6"
                          : "step8"
                        : ""
                    }
                    to={item.to}
                  >
                    <>
                      <span>{item?.image}</span>
                      {t(item.name)}
                    </>
                  </Link>
                )
              )}
            </Accordion>
          </ul>
        </aside>
      )}
    </div>
  );
};

export default memo(SideBar);
